import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import {
  WORD_LENGTH, applyHint, hintPosition, isValidGuess, keyboardState, submitGuess,
  type GameState, type KeyState, type SubmitError,
} from '@lexicon/core';
import type { Strings } from '../i18n';
import { en } from '../i18n/en';
import { useStrings } from '../state/AppContext';

/** Animation clock (spec §5). The hook owns it so the UI stays declarative. */
export const REVEAL_FLIP_MS = 400;
export const REVEAL_STAGGER_MS = 250;

/** How long a row of `length` tiles takes to finish flipping. */
export function revealTotalMs(length: number): number {
  return (length - 1) * REVEAL_STAGGER_MS + REVEAL_FLIP_MS;
}

/** The 5-letter reveal duration. Legacy export — the hook measures per game. */
export const REVEAL_TOTAL_MS = revealTotalMs(WORD_LENGTH);
export const SHAKE_MS = 500;
export const TOAST_MS = 2000;

/**
 * The English rejection copy. Legacy export: the hook itself reads the
 * current language's table (`useStrings`), and this stays for the tests
 * and callers that assert the English wording.
 */
export const SUBMIT_MESSAGES: Record<SubmitError, string> = {
  'invalid-word': en.toast.invalidWord,
  'bad-length': en.toast.badLength,
  'game-over': en.toast.gameOver,
};

/** Rejection copy in the language the board is being played in. */
function submitMessages(t: Strings): Record<SubmitError, string> {
  return {
    'invalid-word': t.toast.invalidWord,
    'bad-length': t.toast.badLength,
    'game-over': t.toast.gameOver,
  };
}

/**
 * Shown when Enter lands before this length's accepted-guess chunk does
 * (spec §10.2/§10.5). Deliberately not a rejection: the row is not
 * consumed and the board does not shake, because nothing is wrong with
 * the guess — we simply cannot check it yet.
 */
export const VALIDATION_PENDING_MESSAGE = en.toast.validationPending;

export interface ToastMessage {
  id: number;
  text: string;
}

export interface GameView {
  game: GameState;
  /** Letters typed into the active row, not yet submitted. */
  draft: string;
  /** Row index currently flipping, or null. */
  revealingRow: number | null;
  shaking: boolean;
  toast: ToastMessage | null;
  /** True once the final reveal has completed (or on a restored finished game). */
  finished: boolean;
  toastSeq: number;
}

type Action =
  | { type: 'reset'; game: GameState }
  | { type: 'letter'; letter: string }
  | { type: 'backspace' }
  | { type: 'reject'; text: string }
  | { type: 'shake-end' }
  | { type: 'accept'; game: GameState }
  | { type: 'reveal-end' }
  | { type: 'hint'; game: GameState }
  | { type: 'toast'; text: string }
  | { type: 'toast-dismiss' };

function initialView(game: GameState): GameView {
  return {
    game,
    draft: '',
    revealingRow: null,
    shaking: false,
    toast: null,
    finished: game.status !== 'playing',
    toastSeq: 0,
  };
}

function withToast(view: GameView, text: string): GameView {
  const id = view.toastSeq + 1;
  return { ...view, toastSeq: id, toast: { id, text } };
}

function acceptsInput(view: GameView): boolean {
  return view.game.status === 'playing' && view.revealingRow === null;
}

function reducer(view: GameView, action: Action): GameView {
  switch (action.type) {
    case 'reset':
      return initialView(action.game);
    case 'letter':
      if (!acceptsInput(view) || view.draft.length >= view.game.answer.length) return view;
      return { ...view, draft: view.draft + action.letter };
    case 'backspace':
      if (!acceptsInput(view) || view.draft.length === 0) return view;
      return { ...view, draft: view.draft.slice(0, -1) };
    case 'reject':
      return withToast({ ...view, shaking: true }, action.text);
    case 'shake-end':
      return view.shaking ? { ...view, shaking: false } : view;
    case 'accept':
      return {
        ...view,
        game: action.game,
        draft: '',
        revealingRow: action.game.guesses.length - 1,
        toast: null,
      };
    case 'reveal-end':
      return { ...view, revealingRow: null, finished: view.game.status !== 'playing' };
    case 'hint':
      return { ...view, game: action.game };
    case 'toast':
      return withToast(view, action.text);
    case 'toast-dismiss':
      return { ...view, toast: null };
  }
}

export interface UseGameOptions {
  /** Restored or freshly created state. Must be referentially stable. */
  initial: GameState;
  /** `null` means "the word list for this length has not loaded yet". */
  isValid?: ((word: string) => boolean) | null;
  /** Fires when a submit was blocked by a not-yet-loaded word list. */
  onValidationPending?: () => void;
  onGuess?: (game: GameState) => void;
  onHintApplied?: (game: GameState) => void;
  /**
   * Fires whenever `view.finished` is (or becomes) true for the current
   * `game` — on the final reveal, on mount with an already-finished
   * `initial`, on a React StrictMode double-invoke, and again on every
   * later remount of that same finished game (e.g. a reload landing back
   * on a completed daily). It is NOT a one-shot "just finished" event.
   * Handlers MUST be idempotent — non-idempotent one-shot effects
   * (confetti, analytics pings) need to guard themselves, e.g. by keying
   * off whether a result was already persisted for this game.
   */
  onFinished?: (game: GameState) => void;
  /**
   * Fires the moment a guess is rejected — the same instant the row starts
   * shaking, for both "not in word list" and "not enough letters".
   */
  onInvalid?: () => void;
  /**
   * Fires once when a game that was in progress is *won*. Deliberately not
   * `onFinished`: that one also fires for an already-finished game restored
   * on mount, which would re-buzz every time a completed daily is reopened.
   */
  onWin?: () => void;
}

export interface UseGame {
  view: GameView;
  keys: Record<string, KeyState>;
  canType: boolean;
  typeLetter: (letter: string) => void;
  backspace: () => void;
  submit: () => void;
  /** Applies a hint. `false` means nothing was revealed — charge no quota. */
  revealHint: () => boolean;
  showToast: (text: string) => void;
}

export function useGame(options: UseGameOptions): UseGame {
  const { initial, isValid = isValidGuess } = options;
  const [view, dispatch] = useReducer(reducer, initial, initialView);
  // Held in a ref like the callbacks below, because `submit` is a stable
  // `useCallback` with no dependencies: closing over `t` directly would
  // pin the toast copy to whatever language was current when the board
  // first mounted.
  const t = useStrings();
  const messagesRef = useRef(submitMessages(t));
  messagesRef.current = submitMessages(t);
  const pendingMessageRef = useRef(t.toast.validationPending);
  pendingMessageRef.current = t.toast.validationPending;

  // Latest callbacks/validator without re-arming timers or re-creating actions.
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const isValidRef = useRef(isValid);
  isValidRef.current = isValid;
  const viewRef = useRef(view);
  viewRef.current = view;

  // `onWin` fires on the *transition* into a won game, never on a restored
  // one: armed only while the game is actually in progress, and disarmed
  // after one shot.
  const winCallback = useRef({ armed: initial.status === 'playing', fired: false });

  // Only a *different* game (new day, new challenge) resets the view.
  const mountedInitial = useRef(initial);
  useEffect(() => {
    if (mountedInitial.current === initial) return;
    mountedInitial.current = initial;
    winCallback.current = { armed: initial.status === 'playing', fired: false };
    dispatch({ type: 'reset', game: initial });
  }, [initial]);

  useEffect(() => {
    if (view.revealingRow === null) return undefined;
    const timer = window.setTimeout(
      () => dispatch({ type: 'reveal-end' }),
      revealTotalMs(view.game.answer.length),
    );
    return () => window.clearTimeout(timer);
  }, [view.revealingRow, view.game.answer]);

  useEffect(() => {
    if (!view.shaking) return undefined;
    optionsRef.current.onInvalid?.();
    const timer = window.setTimeout(() => dispatch({ type: 'shake-end' }), SHAKE_MS);
    return () => window.clearTimeout(timer);
  }, [view.shaking]);

  useEffect(() => {
    if (view.toast === null) return undefined;
    const timer = window.setTimeout(() => dispatch({ type: 'toast-dismiss' }), TOAST_MS);
    return () => window.clearTimeout(timer);
  }, [view.toast?.id]);

  useEffect(() => {
    if (!view.finished) return;
    // finalizeGame is idempotent, so a StrictMode double-fire is safe.
    optionsRef.current.onFinished?.(view.game);

    const win = winCallback.current;
    if (win.armed && !win.fired && view.game.status === 'won') {
      win.fired = true;
      optionsRef.current.onWin?.();
    }
  }, [view.finished, view.game]);

  const typeLetter = useCallback((letter: string) => {
    if (!/^[a-zA-Z]$/.test(letter)) return;
    dispatch({ type: 'letter', letter: letter.toLowerCase() });
  }, []);

  const backspace = useCallback(() => {
    dispatch({ type: 'backspace' });
  }, []);

  const submit = useCallback(() => {
    const current = viewRef.current;
    if (!acceptsInput(current)) return;
    if (current.draft.length < current.game.answer.length) {
      dispatch({ type: 'reject', text: messagesRef.current['bad-length'] });
      return;
    }
    const isValid = isValidRef.current;
    if (isValid === null) {
      optionsRef.current.onValidationPending?.();
      dispatch({ type: 'toast', text: pendingMessageRef.current });
      return;
    }
    const result = submitGuess(current.game, current.draft, isValid);
    if (!result.ok) {
      dispatch({ type: 'reject', text: messagesRef.current[result.error] });
      return;
    }
    dispatch({ type: 'accept', game: result.state });
    optionsRef.current.onGuess?.(result.state);
  }, []);

  const revealHint = useCallback((): boolean => {
    const current = viewRef.current;
    if (!acceptsInput(current)) return false;
    if (hintPosition(current.game) === null) return false;
    const next = applyHint(current.game);
    dispatch({ type: 'hint', game: next });
    optionsRef.current.onHintApplied?.(next);
    return true;
  }, []);

  const showToast = useCallback((text: string) => {
    dispatch({ type: 'toast', text });
  }, []);

  // The flipping row stays uncoloured on the keyboard until its reveal ends.
  const keys = useMemo(() => {
    const upTo = view.revealingRow === null ? view.game.guesses.length : view.revealingRow;
    return keyboardState(view.game.guesses.slice(0, upTo));
  }, [view.game.guesses, view.revealingRow]);

  return {
    view,
    keys,
    canType: acceptsInput(view),
    typeLetter,
    backspace,
    submit,
    revealHint,
    showToast,
  };
}
